import React from 'react';
import PropTypes from 'prop-types';

// Constants
const STATUS_CONTAINER_CLASS = 'status-container';
const PURCHASE_ORDER_TYPE = 'purchase-order';
const DOCUMENT_TYPE = 'document';

/**
 * StatusCell component for displaying status with visual indicators
 * @param {Object} props - Component props
 * @param {string} props.status - The status value to display
 * @param {Function} props.t - Translation function
 * @param {string} props.type - Type of status cell ('purchase-order' or 'document')
 * @returns {JSX.Element} StatusCell component
 */
function StatusCell({ status, t, type = PURCHASE_ORDER_TYPE }) {
  // Error handling - ensure t function exists
  if (!t || typeof t !== 'function') {
    // eslint-disable-next-line no-console
    console.error("StatusCell: Translation function 't' is required");
    return <div className={STATUS_CONTAINER_CLASS}>-</div>;
  }

  const normalizedStatus = status?.toString().toLowerCase() || '';

  // For document status (files in PurchaseOrderDocuments)
  if (type === DOCUMENT_TYPE) {
    if (normalizedStatus === 'approved') {
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.APPROVED')}</span>
          <span className="col-status right approved" />
        </div>
      );
    }

    if (normalizedStatus === 'rejected') {
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.REJECTED')}</span>
          <span className="col-status right rejected" />
        </div>
      );
    }

    // Default case for empty or unknown document status
    return (
      <div className="status-container">
        <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.DRAFT')}</span>
        <span className="col-status right draft" />
      </div>
    );
  }

  // For purchase order status (main table)
  switch (normalizedStatus) {
    case 'approved':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.APPROVED')}</span>
          <span className="col-status right approved" />
        </div>
      );

    case 'rejected':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.REJECTED')}</span>
          <span className="col-status right rejected" />
        </div>
      );

    case 'active':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.ACTIVE')}</span>
          <span className="col-status right pending" />
        </div>
      );

    case 'revised':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.REVISED')}</span>
          <span className="col-status right revised" />
        </div>
      );

    case 'cancelled':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.CANCELLED')}</span>
          <span className="col-status right rejected" />
        </div>
      );

    case 'delivered':
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.DELIVERED')}</span>
          <span className="col-status right approved" />
        </div>
      );

    default:
      return (
        <div className={STATUS_CONTAINER_CLASS}>
          <span className="col-status right draft" />
          <span>{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER.STATUS_VALUES.DRAFT')}</span>
        </div>
      );
  }
}

StatusCell.propTypes = {
  status: PropTypes.string,
  t: PropTypes.func.isRequired,
  type: PropTypes.oneOf([PURCHASE_ORDER_TYPE, DOCUMENT_TYPE]),
};

StatusCell.defaultProps = {
  status: '',
  type: PURCHASE_ORDER_TYPE,
};

export default React.memo(StatusCell);
